import { Check } from 'lucide-react';
import { Link } from 'react-router';
import { cn } from '@/lib/utils';

interface Step {
  id: string;
  title: string;
  href: string;
  completed?: boolean;
  disabled?: boolean;
  isActive?: boolean;
}

interface StepNavigationProps {
  steps: Step[];
  className?: string;
}

export function StepNavigation({ steps, className }: StepNavigationProps) {
  // Calculate progress percentage based on completed steps
  const completedStepsCount = steps.filter((step) => step.completed).length;
  const progressPercentage = steps.length > 1 ? (completedStepsCount / steps.length) * 100 : 0;

  const getStepStatus = (index: number) => {
    const step = steps[index];
    const isActive = step.isActive !== undefined ? step.isActive : false;
    const isCompleted = step.completed && !isActive;

    // Step is clickable if the hook says it's not disabled.
    // All ordering and content gates are handled by the parent hook's disabled property.
    const isClickable = !step.disabled;

    return { isActive, isCompleted, isClickable };
  };

  // Calculate line offset based on number of steps (centers line on first/last step circles)
  const lineOffset = `${50 / steps.length}%`;

  return (
    <nav className={cn('w-full', className)} aria-label="Progresso das etapas">
      <div className="relative">
        {/* Progress line background */}
        <div
          className="absolute top-6 h-0.5 bg-gray-200"
          style={{ left: lineOffset, right: lineOffset }}
        >
          <div
            className="h-full bg-green-500 transition-all duration-500 ease-out"
            style={{
              width: `${progressPercentage}%`,
            }}
          />
        </div>

        {/* Steps container */}
        <div className="flex items-start justify-between relative">
          {steps.map((step, index) => {
            const { isActive, isCompleted, isClickable } = getStepStatus(index);
            const stepNumber = index + 1;

            const StepContent = (
              <div key={step.id} className="flex flex-col items-center group relative">
                {/* Step circle */}
                <div
                  className={cn(
                    'w-12 h-12 rounded-full flex items-center justify-center text-sm font-semibold transition-all duration-200 mb-3 relative z-10 border-2',
                    // Completed state
                    isCompleted && 'bg-green-500 border-green-500 text-white',
                    // Active state - solid black
                    isActive && 'bg-gray-800 border-gray-800 text-white',
                    // Default/inactive state
                    !isCompleted && !isActive && 'bg-gray-100 border-gray-200 text-gray-400',
                    // Hover effects for clickable steps
                    isClickable && !isActive && 'hover:border-gray-300',
                    // Non-clickable steps
                    !isClickable && 'opacity-50',
                  )}
                >
                  {isCompleted ? <Check className="w-5 h-5" /> : <span>{stepNumber}</span>}
                </div>

                {/* Step title */}
                <span
                  className={cn(
                    'text-sm font-medium transition-colors duration-200',
                    isActive && 'text-gray-900',
                    isCompleted && 'text-green-600',
                    !isCompleted && !isActive && 'text-gray-400',
                  )}
                >
                  {step.title}
                </span>
              </div>
            );

            return isClickable ? (
              <Link
                key={step.id}
                to={step.href}
                className="flex-1 flex justify-center p-2 focus:outline-none"
                aria-current={isActive ? 'step' : undefined}
              >
                {StepContent}
              </Link>
            ) : (
              <div
                key={step.id}
                className="flex-1 flex justify-center cursor-not-allowed p-2"
                title="Complete a etapa anterior"
              >
                {StepContent}
              </div>
            );
          })}
        </div>
      </div>
    </nav>
  );
}
